
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Report } from "@/types/report";

// Define a type for the raw data from Supabase
interface RawReportData {
  id: string;
  teaching_date: string;
  teaching_time_slot: string;
  student_nickname: string;
  student_full_name: string;
  student_grade_level: string;
  teacher_display: string;
  teacher_id: string;
  subject: string;
  worksheet_urls: string[];
  worksheet_drive_link?: string;
  development_report?: string;
  teaching_content?: string;
  approved_worksheet_status?: string;
  approved_worksheet_at?: string;
  approved_worksheet_by?: string;
  approved_worksheet_note?: string;
  other_subject?: boolean;
}

interface UseAdminWorksheetCheckParams {
  page: number;
  itemsPerPage: number;
  searchQuery: string;
  statusFilter: "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT";
  showOtherSubjectsOnly: boolean;
  sortField: string;
  sortDirection: "asc" | "desc";
}

export const useAdminWorksheetCheck = (params: UseAdminWorksheetCheckParams) => {
  const query = useQuery({
    queryKey: ["admin-worksheet-check", params],
    queryFn: async () => {
      try {
        // Calculate pagination
        const from = (params.page - 1) * params.itemsPerPage;
        const to = from + params.itemsPerPage - 1;

        // Start building the query
        let query = supabase
          .from("student_development_reports")
          .select(
            "id, teaching_date, teaching_time_slot, student_nickname, student_full_name, student_grade_level, teacher_display, teacher_id, subject, worksheet_urls, worksheet_drive_link, development_report, teaching_content, approved_worksheet_status, approved_worksheet_at, approved_worksheet_by, approved_worksheet_note, other_subject",
            { count: "exact" }
          )
          .filter("worksheet_urls", "not.is", null)
          .not("worksheet_urls", "eq", "{}");

        // Apply search filter
        if (params.searchQuery.trim()) {
          const searchTerm = `%${params.searchQuery.toLowerCase()}%`;
          query = query.or(`student_full_name.ilike.${searchTerm},student_nickname.ilike.${searchTerm},teacher_display.ilike.${searchTerm},subject.ilike.${searchTerm},teaching_content.ilike.${searchTerm}`);
        }

        // Apply status filter
        if (params.statusFilter !== "ALL") {
          if (params.statusFilter === "WAITING") {
            query = query.or("approved_worksheet_status.is.null,approved_worksheet_status.eq.WAITING");
          } else {
            query = query.eq("approved_worksheet_status", params.statusFilter);
          }
        }

        // Apply other subjects filter
        if (params.showOtherSubjectsOnly) {
          query = query.eq("other_subject", true);
        }

        // Apply sorting
        const ascending = params.sortDirection === "asc";
        if (params.sortField === "approved_worksheet_status") {
          // For status sorting, we need to handle null values
          query = query.order("approved_worksheet_status", { ascending, nullsFirst: ascending });
        } else {
          query = query.order(params.sortField, { ascending });
        }

        // Apply pagination and get results
        const { data, error, count } = await query.range(from, to);

        if (error) {
          console.error('Error fetching worksheet check data:', error);
          return { data: [], totalCount: 0 };
        }

        const formattedData = (data || []).map((report: RawReportData) => ({
          ...report,
          topic_learned: report.teaching_content || "-",
          created_at: "",
          status: "",
          start_date: null,
          end_date: null,
          approved_worksheet_status: report.approved_worksheet_status === "APPROVED"
            ? "APPROVED"
            : report.approved_worksheet_status === "REJECTED"
            ? "REJECTED"
            : report.approved_worksheet_status === "NO_PROBLEM_REPORT"
            ? "NO_PROBLEM_REPORT"
            : "WAITING"
        }));

        return { data: formattedData, totalCount: count || 0 };
      } catch (error) {
        console.error('Error fetching worksheet check data:', error);
        return { data: [], totalCount: 0 };
      }
    },
  });

  return {
    ...query,
    reports: (query.data?.data || []) as unknown as Report[],
    totalCount: query.data?.totalCount || 0,
    refetch: query.refetch,
  };
};
